import { jahrgangFuer } from "./fachlogik.js";
import { NV } from "./konstanten.js";


/* ═══════════════════════════════════════════════════════════════
   Offene Kritiken — erst notieren, später zuordnen

   Eine Verkostung passiert selten am Schreibtisch. Man steht mit dem
   Glas in der Hand, das Etikett ist fremd, der Winzer unbekannt —
   und bis der Wein sauber im Kellerbuch stünde, ist der Eindruck
   verflogen. Deshalb darf eine Kritik ohne Wein existieren: mit einem
   frei getippten Namen, einem Foto des Etiketts, und sonst nichts.

   Eine solche Kritik trägt `weinId: null`. Daran hängt alles Weitere:

   - `kritikenFuer()` in fachlogik.js filtert über `k.weinId === weinId`
     und lässt sie damit von sich aus überall dort weg, wo Kritiken zu
     einem Wein gehören (Kellerliste, Weinseite, Weinkarte, Statistik).
     Es braucht keine zusätzliche Ausnahme an diesen Stellen.
   - Der Trinkverlauf ist die einzige Ansicht, die sie zeigt — dort
     gehören sie hin, denn dort ist die Chronik, und dort führt der
     Weg zurück zur Zuordnung.

   Das Foto liegt solange an der Kritik. Es ist bereits auf dem Server
   und behält beim Zuordnen seinen Pfad — es wird nur umgehängt, nicht
   neu geladen.
   ═══════════════════════════════════════════════════════════════ */


/** Kritik ohne Wein — noch nicht zugeordnet. */
const istOffeneKritik = (k) => !!k && !k.weinId;


/** Alle offenen Kritiken, jüngste zuerst. */
function offeneKritiken(db) {
  return ((db && db.kritiken) || [])
    .filter(istOffeneKritik)
    .sort((a, b) => String(b.erfasst || "").localeCompare(String(a.erfasst || "")));
}


/** Wie eine offene Kritik im Verlauf benannt wird. */
const kritikBezeichnung = (k) => (k && String(k.weinText || "").trim()) || "";


/** Trägt der Jahrgang bereits ein Etikett? Dann darf das Foto der
    Kritik es nicht stillschweigend verdrängen — der Nutzer entscheidet. */
function jahrgangHatEtikett(db, weinId, jgId) {
  const wein = ((db && db.weine) || []).find((w) => w.id === weinId);
  if (!wein) return false;
  const jg = jahrgangFuer(wein, jgId, null);
  return !!(jg && jg.foto);
}


/** Welches Foto beim Zuordnen übrig bleibt und vom Server gelöscht
    werden kann — entweder das der Kritik (wenn es nicht übernommen
    wird) oder das bisherige des Jahrgangs (wenn es ersetzt wird).
    Gibt die Pfade zurück, nicht das Foto: nur die braucht der
    Aufräumlauf. */
function verdraengteFotoPfade(db, kritik, { weinId, jgId, fotoUebernehmen }) {
  const kFoto = kritik ? kritik.foto : null;
  if (!kFoto) return [];
  if (!fotoUebernehmen) return [kFoto.pfad, kFoto.thumb].filter(Boolean);
  const wein = ((db && db.weine) || []).find((w) => w.id === weinId);
  const jg = wein ? jahrgangFuer(wein, jgId, null) : null;
  const alt = jg && jg.foto;
  return alt ? [alt.pfad, alt.thumb].filter(Boolean) : [];
}


/**
 * Eine offene Kritik einem Jahrgang zuschlagen.
 *
 * Verändert zwei Dinge zugleich, deshalb steht es hier als ein
 * Vorgang: die Kritik bekommt ihren Wein, und das an ihr hängende
 * Etikettenfoto wandert an den Jahrgang. Getrennt ausgeführt gäbe es
 * einen Zwischenzustand, in dem das Foto an keinem von beiden hängt.
 *
 * `fotoUebernehmen` entscheidet über ein bereits vorhandenes Etikett
 * des Jahrgangs. Die Felder `weinText` und `foto` der Kritik fallen
 * in jedem Fall weg — sie waren nur Behelf bis zu diesem Moment.
 */
function kritikZuordnen(db, kritikId, { weinId, jgId, fotoUebernehmen = true }) {
  if (!db) return db;
  const kritik = (db.kritiken || []).find((k) => k.id === kritikId);
  if (!kritik) return db;
  const wein = (db.weine || []).find((w) => w.id === weinId);
  if (!wein) return db;
  const jg = jahrgangFuer(wein, jgId, null);
  if (!jg) return db;

  const uebernehmen = !!(kritik.foto && fotoUebernehmen);

  const weine = uebernehmen
    ? db.weine.map((w) => w.id !== wein.id ? w : {
        ...w,
        jahrgaenge: w.jahrgaenge.map((j) => j.id !== jg.id ? j : { ...j, foto: kritik.foto }),
      })
    : db.weine;

  const kritiken = db.kritiken.map((k) => {
    if (k.id !== kritikId) return k;
    const { weinText, foto, ...rest } = k;
    return { ...rest, weinId: wein.id, jgId: jg.id, jahr: jg.jahr != null ? jg.jahr : NV };
  });

  return { ...db, weine, kritiken };
}


export {
  istOffeneKritik, jahrgangHatEtikett, kritikBezeichnung, kritikZuordnen,
  offeneKritiken, verdraengteFotoPfade,
};
